/**
 * Step-up for sensitive calls (secret reveal/update).
 *
 * The agent answers such calls with 403 `reason: reverification_required`
 * when the bearer's `auth_time` is older than ten minutes. This hook turns
 * that refusal into one dialog: confirming sends the browser to the realm
 * with `prompt=login`, which refreshes `auth_time`, and returns to the page
 * the operator was on. The call is not replayed; the operator repeats it.
 */
import { useRef, useState, type ReactNode } from 'react';

import { ApiError } from '@optum/api-client/client';

import { stepUp } from '../auth/oidc.js';
import { Button } from '../ui/Button.js';
import { Dialog, DialogActions } from '../ui/Dialog.js';

/** Matches the agent's FRESH_PASSWORD_MAX_AGE_MINUTES backstop. */
const MAX_AGE_MINUTES = 10;

function needsStepUp(e: unknown): boolean {
  return (
    e instanceof ApiError && e.status === 403 && e.details?.['reason'] === 'reverification_required'
  );
}

/** The page to come back to after the realm round trip. */
function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function useStepUp<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
): [(...args: A) => Promise<R>, ReactNode] {
  const [dialogOpen, setDialogOpen] = useState(false);
  const pending = useRef<{ reject: (e: Error) => void } | null>(null);

  async function run(...args: A): Promise<R> {
    try {
      return await fn(...args);
    } catch (e) {
      if (!needsStepUp(e)) throw e;
      // The promise settles only on cancel: confirming leaves the page.
      return await new Promise<R>((_resolve, reject) => {
        pending.current = { reject };
        setDialogOpen(true);
      });
    }
  }

  function cancel(): void {
    setDialogOpen(false);
    pending.current?.reject(new Error('Sign-in required.'));
    pending.current = null;
  }

  const dialog = (
    <Dialog
      open={dialogOpen}
      onClose={cancel}
      title="Sign in again to continue"
      subtitle={
        <>
          Revealing or changing secrets requires a sign-in within the last {MAX_AGE_MINUTES}{' '}
          minutes. You will be sent to sign in again and brought back to this page; then repeat the
          action.
        </>
      }
    >
      <DialogActions>
        <Button type="button" variant="ghost" onClick={cancel}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={() => void stepUp(currentPath())}>
          Sign in again
        </Button>
      </DialogActions>
    </Dialog>
  );

  return [run, dialog];
}
